/**
 * Westmoreland V2 Theme Configuration - Soft Modern Design
 *
 * Elevated design featuring:
 * - Layered shadows instead of borders
 * - Gradient buttons with glow effects
 * - Glass morphism overlays
 * - Refined typography and spacing
 *
 * Design System: Soft Modern V2
 * Author: SchoolVision Dev
 * Date: December 2025
 */

export const westmoreland2Theme = {
  name: 'westmoreland2',
  brandName: 'Westmoreland County Community College',
  logoPath: 'westmoreland.png',

  // Design System Version Identifier
  designSystem: 'soft-modern-v2',

  colors: {
    // Core palette (same hues as V1, refined application)
    headerColor: '#7EB3D7',
    headerHover: '#5a9bc7',
    frameColor: '#79bce1',
    buttonColor: '#0c85b6',
    buttonHover: '#0a6b94',
    highlightButtonColor: '#992222',
    highlightButtonHover: '#7a1b1b',
    headerButtonColor: '#992222',
    textColor: '#0E2049',
    lightTextColor: '#FFFFFF',
    buttonTextColor: '#FFFFFF',
    headerTextColor: '#FFFFFF',
    headerButtonTextColor: '#699864',
    alertColor: '#E32323',
    alertHover: '#c21e1e',
    successColor: '#699864',
    successHover: '#527a4d',
    inactiveColor: '#C3C3C3',

    // V2 Extended Palette - Gradients
    primaryGradientFrom: '#0c85b6',
    primaryGradientTo: '#0a6b94',
    accentGradientFrom: '#992222',
    accentGradientTo: '#7a1b1b',
    successGradientFrom: '#699864',
    successGradientTo: '#527a4e',
    surfaceGradientFrom: '#ffffff',
    surfaceGradientTo: '#fafafa',

    // V2 Glass Morphism Colors
    glassBackground: 'rgba(255, 255, 255, 0.8)',
    glassBorder: 'rgba(255, 255, 255, 0.5)',
    glassBackgroundDark: 'rgba(0, 0, 0, 0.1)'
  },

  // V2 Shadow System - Soft, layered shadows
  shadows: {
    // Soft shadows for cards and containers
    soft: '0 2px 8px -2px rgba(0,0,0,0.05), 0 4px 20px -4px rgba(0,0,0,0.08)',
    softSm: '0 1px 4px -1px rgba(0,0,0,0.04), 0 2px 8px -2px rgba(0,0,0,0.06)',
    softLg: '0 8px 30px -6px rgba(0,0,0,0.12)',
    softXl: '0 12px 40px -8px rgba(0,0,0,0.15)',

    // Glow shadows for primary actions
    glowPrimary: '0 4px 14px 0 rgba(12,133,182,0.4)',
    glowPrimaryLg: '0 6px 20px 0 rgba(12,133,182,0.5)',

    // Accent glow for highlights
    glowAccent: '0 4px 14px 0 rgba(153,34,34,0.35)',
    glowAccentLg: '0 6px 20px 0 rgba(153,34,34,0.45)',

    // Success glow
    glowSuccess: '0 4px 14px 0 rgba(105,152,100,0.35)'
  },

  // V2 Border Radius System
  radii: {
    sm: '0.5rem',     // 8px - inputs, small elements
    md: '0.75rem',    // 12px - buttons
    lg: '1rem',       // 16px - cards
    xl: '1.5rem',     // 24px - modals, large cards
    '2xl': '2rem',    // 32px - hero elements
    full: '9999px'    // pills, badges, avatars
  },

  // V2 Transition System
  transitions: {
    fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
    normal: '200ms cubic-bezier(0.4, 0, 0.2, 1)',
    slow: '300ms cubic-bezier(0.4, 0, 0.2, 1)',
    bounce: '300ms cubic-bezier(0.34, 1.56, 0.64, 1)'
  },

  components: {
    header: 'GenericHeader',  // Uses V2 styling via CSS classes
    hero: 'GenericHero',
    footer: 'GenericFooter'
  },

  features: {
    // Standard features
    megaMenu: false,
    searchBar: false,
    scrollEffects: false,
    heroSection: false,

    // V2 Design Features
    glassMorphism: true,
    softShadows: true,
    gradientButtons: true,
    hoverAnimations: true,
    glowEffects: true
  },

  contact: {
    email: 'info@westmoreland.edu',
    phone: '(724) 925-4000',
    address: '145 Pavilion Lane, Youngwood, PA 15697'
  },

  branding: {
    tagline: '',
    heroTitle: 'Welcome to the Campus Store',
    heroDescription: 'Shop for all your campus needs',
    heroImage: '/hero-background.jpg'
  }
}
